import { Component, ViewEncapsulation, OnInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { VehicleService } from '../../../services/vehicle/vehicle.service';
import { PaginatedResponse, PaginationInfo } from '../../../shared/models/api-response.interface';
import { InventoryCar } from '../../../shared/models/car.interface';
import { DestructorComponent } from '../../../components/destructor/destructor.component';
import { VehicleExtraService } from 'src/app/services/vehicle-extra/vehicle-extra.service';
import { MashoManagerModalService } from '../../../services/masho-manager-modal/masho-manager-modal.service';
import { GeneralService } from '../../../services/general/general.service';

declare var bootstrap: any;
declare var Swal: any;

@Component({
  selector: 'app-vh-refund',
  templateUrl: './vh-refund.component.html',
  styleUrls: ['./vh-refund.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class VhRefundComponent extends DestructorComponent implements OnInit {

  // Data properties
  vehicles: InventoryCar[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  isExportingExcel: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  hasChanges: boolean = false;
  changedRows: any[] = [];
  showFiltersForm: boolean = false;
  selectedVehicles: string[] = [];

  // Form data for editable fields
  formData: { [vehicleId: string]: any } = {};

  // Filter properties
  filters = {
    stock_number: '',
    chassis_number: '',
    purchase_date_from: '',
    purchase_date_to: '',
    initial_docs_rcvd_date_from: '',
    initial_docs_rcvd_date_to: '',
    masho_date_from: '',
    masho_date_to: '',
    masho_validity_date_from: '',
    masho_validity_date_to: '',
    weight_tax_refund_date_from: '',
    weight_tax_refund_date_to: '',
    etd_from: '',
    etd_to: '',
    recycle_fee_application_date_from: '',
    recycle_fee_application_date_to: '',
    recycle_fee_refund_date_from: '',
    recycle_fee_refund_date_to: '',
    automobile_tax_refund_date_from: '',
    automobile_tax_refund_date_to: '',
    shaken_validity_from: '',
    shaken_validity_to: '',
    car_insurance_cancellation_date_from: '',
    car_insurance_cancellation_date_to: '',
    car_insurance_company_name: '',
    quick_search: '',
    keyword_search: ''
  };

  // Dropdown options
  typeOptions = [
    { label: 'M', value: 'M' },
    { label: 'K', value: 'K' }
  ];

  // Quick search options
  quickSearchOptions = [
    { label: 'R.No', value: 'r_no' },
    { label: 'R.YES', value: 'r_yes' },
    { label: 'Finish', value: 'finish' },
    { label: 'All', value: 'all' }
  ];

  // Expose Math to template
  Math = Math;

  constructor(
    private _vehicleService: VehicleService,
    private _vehicleExtraService: VehicleExtraService,
    private _generalService: GeneralService,
    private cdr: ChangeDetectorRef,
    private _mashoManagerModalService: MashoManagerModalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVehicles();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadVehicles(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    const apiFilters: Record<string, any> = { ...filters };

    apiFilters.append_type = 'refund';
    apiFilters.panel_filters = 'true';

    for(const key in apiFilters) {
      if (apiFilters[key] === '' || apiFilters[key] === null) {
        delete apiFilters[key];
      }
    }
    this.subscriptions.push(
      this._vehicleService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<InventoryCar>) => {
          if (response.status && response.data) {
            this.vehicles = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.initializeFormData();
            // Clear selected vehicles when loading new data
            this.selectedVehicles = [];
          } else {
            this.vehicles = [];
            this.resetPagination();
            this.selectedVehicles = [];
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading vehicles:', error);
          this.vehicles = [];
          this.resetPagination();
          this.selectedVehicles = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private initializeFormData(): void {
    this.formData = {};
    this.vehicles.forEach(vehicle => {
      // Get existing vehicle_extras data if available
      const existingData = vehicle.vehicle_extras || {};
      const mashoData = vehicle.masho || {};

      this.formData[vehicle._id] = {
        initial_docs_rcvd_date: existingData.initial_docs_rcvd_date || '',
        type: existingData.type || '',
        plate_have: existingData.plate_have || false,
        plate_no: existingData.plate_no || '',
        plate_received_date: existingData.plate_received_date || '',
        shaken_validity: existingData.shaken_validity || '',
        massho_date: mashoData.masho_date || '',
        massho_validity: mashoData.valid_until_date || '',
        recycle_certificate_no: existingData.recycle_certificate_no || '',
        recycle_fee_applied_date: existingData.recycle_fee_applied_date || '',
        recycle_fee_refund: existingData.recycle_fee_refund || '',
        recycle_fee_refund_date: existingData.recycle_fee_refund_date || '',
        weight_tax: existingData.weight_tax || '',
        weight_tax_refund_date: existingData.weight_tax_refund_date || '',
        automobile_tax_refund: existingData.automobile_tax_refund || '',
        automobile_tax_refund_date: existingData.automobile_tax_refund_date || '',
        car_insurance_company: existingData.car_insurance_company || '',
        car_insurance_certificate_no: existingData.car_insurance_certificate_no || '',
        car_insurance_cancellation_date: existingData.car_insurance_cancellation_date || '',
        car_insurance_refund: existingData.car_insurance_refund || '',
        mehen_fax: existingData.mehen_fax || false,
        memo: existingData.memo || ''
      };
    });
  }

  onPageChange(page: number): void {
    this.loadVehicles(page, this.searchTerm, this.filters);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadVehicles(1, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    console.log('Applied filters:', this.filters);
    this.loadVehicles(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      stock_number: '',
      chassis_number: '',
      purchase_date_from: '',
      purchase_date_to: '',
      initial_docs_rcvd_date_from: '',
      initial_docs_rcvd_date_to: '',
      masho_date_from: '',
      masho_date_to: '',
      masho_validity_date_from: '',
      masho_validity_date_to: '',
      weight_tax_refund_date_from: '',
      weight_tax_refund_date_to: '',
      etd_from: '',
      etd_to: '',
      recycle_fee_application_date_from: '',
      recycle_fee_application_date_to: '',
      recycle_fee_refund_date_from: '',
      recycle_fee_refund_date_to: '',
      automobile_tax_refund_date_from: '',
      automobile_tax_refund_date_to: '',
      shaken_validity_from: '',
      shaken_validity_to: '',
      car_insurance_cancellation_date_from: '',
      car_insurance_cancellation_date_to: '',
      car_insurance_company_name: '',
      quick_search: '',
      keyword_search: ''
    };
    this.searchTerm = '';
    this.loadVehicles(1, '', {});
  }

  onFieldChange(vehicleId: string, field: string, value: any): void {
    if (!this.formData[vehicleId]) {
      this.formData[vehicleId] = {};
    }

    this.formData[vehicleId][field] = value;

    // Check if this vehicle is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row.vehicle_id === vehicleId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex] = {
        vehicle_id: vehicleId,
        ...this.formData[vehicleId]
      };
    } else {
      // Add new changed row
      this.changedRows.push({
        vehicle_id: vehicleId,
        ...this.formData[vehicleId]
      });
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  onSave(): void {
    if (this.changedRows.length === 0) {
      return;
    }

    const payload = {
      vehicle_extras: this.changedRows
    };

    this.isLoading = true;

    this._vehicleExtraService.AddData(payload).subscribe({
      next: (response) => {
        console.log('Vehicle extras saved successfully:', response);
        // Reset changes after successful save
        this.hasChanges = false;
        this.changedRows = [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error saving vehicle extras:', error);
        this.isLoading = false;
      }
    });
  }

  // Helper methods for displaying data
  formatPrice(price: number): string {
    if (!price) return '-';
    return '¥' + price.toLocaleString();
  }

  // Get display value or fallback
  getDisplayValue(value: any, fallback: string = '-'): string {
    return value || fallback;
  }

  // Helper methods for form data access
  getFormValue(vehicleId: string, field: string): any {
    return this.formData[vehicleId]?.[field] || '';
  }

  setFormValue(vehicleId: string, field: string, value: any): void {
    if (!this.formData[vehicleId]) {
      this.formData[vehicleId] = {};
    }
    this.formData[vehicleId][field] = value;
    this.onFieldChange(vehicleId, field, value);
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: InventoryCar): string {
    return item._id || item.inventory_no || index.toString();
  }

  // Modal opening methods
  openMashoModal(vehicle: any): void {
    if (vehicle && vehicle._id) {
      // Set the pre-selected vehicle in the modal service
      this._mashoManagerModalService.setPreSelectedVehicle(vehicle);

      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // Open the masho manager add/edit modal
        const modalElement = document.getElementById('kt_modal_masho_manager_add_edit');

        if (modalElement) {
          try {
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
          } catch (error) {
            console.error('Error opening Masho modal:', error);
          }
        } else {
          console.error('Masho modal element not found');
        }
      }, 100);
    }
  }

  openDocumentsModal(vehicle: any): void {
    console.log('Opening Documents modal for vehicle:', vehicle);
    if (vehicle && vehicle._id) {
      // Set the selected vehicle for the modal
      (window as any).selectedVehicleForDocuments = vehicle;

      // Open the document upload modal
      const modalElement = document.getElementById('kt_modal_vehicle_upload_documents');

      if (modalElement) {
        try {
          const modal = new bootstrap.Modal(modalElement);
          modal.show();
          console.log('Documents modal opened successfully');
        } catch (error) {
          console.error('Error opening Documents modal:', error);
        }
      } else {
        console.error('Documents modal element not found');
      }
    }
  }

  // Checkbox selection methods
  isVehicleSelected(vehicleId: string): boolean {
    return this.selectedVehicles.includes(vehicleId);
  }

  toggleVehicleSelection(vehicleId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedVehicles.includes(vehicleId)) {
        this.selectedVehicles.push(vehicleId);
      }
    } else {
      this.selectedVehicles = this.selectedVehicles.filter(id => id !== vehicleId);
    }
  }

  isAllSelected(): boolean {
    return this.vehicles.length > 0 && this.selectedVehicles.length === this.vehicles.length;
  }

  isIndeterminate(): boolean {
    return this.selectedVehicles.length > 0 && this.selectedVehicles.length < this.vehicles.length;
  }

  toggleSelectAll(event: any): void {
    if (event.target.checked) {
      this.selectedVehicles = this.vehicles.map(vehicle => vehicle._id);
    } else {
      this.selectedVehicles = [];
    }
  }

  copySelectedISL(): void {
    if (this.selectedVehicles.length === 0) {
      return;
    }

    // Get car_no values for selected vehicles
    const selectedCarNos = this.vehicles
      .filter(vehicle => this.selectedVehicles.includes(vehicle._id))
      .map(vehicle => vehicle.car_no || vehicle.stock_no)
      .filter(carNo => carNo) // Remove empty values
      .join(',');

    if (selectedCarNos) {
      // Copy to clipboard
      navigator.clipboard.writeText(selectedCarNos).then(() => {
        // Show success message (you can replace this with your preferred notification method)
        console.log('ISL numbers copied to clipboard:', selectedCarNos);
        alert(`${this.selectedVehicles.length} ISL numbers copied to clipboard!`);
      }).catch(err => {
        console.error('Failed to copy ISL numbers:', err);
        alert('Failed to copy ISL numbers to clipboard');
      });
    }
  }

  // Modal event handlers
  onMashoAdded(): void {
    // Optionally reload vehicles to reflect changes
    this.loadVehicles();
  }

  onMashoUpdated(): void {
    // Optionally reload vehicles to reflect changes
    this.loadVehicles();
  }

  onDocumentsUploaded(): void {
    // Optionally reload vehicles to reflect changes
    this.loadVehicles();
  }

  getRecycleFeeDifference(vehicle: any): number {

    const recycleFeePaid = vehicle.recycle_fee_paid || 0;
    const recycleFeeRefund = this.getFormValue(vehicle._id, 'recycle_fee_refund') || 0;
    return recycleFeePaid - (+recycleFeeRefund);

  }

  getAutomobileTaxDifference(vehicle: any): number {
    const automobileTaxPaid = vehicle.automobile_tax || 0;
    const automobileTaxRefund = this.getFormValue(vehicle._id, 'automobile_tax_refund') || 0;
    return automobileTaxPaid - (+automobileTaxRefund);
  }

  getGrandTotalRefund(vehicle: any): number {

    let total = 0;

    if(!vehicle.vehicle_extras) return total;

    const recycleFeeRefund = this.getFormValue(vehicle._id, 'recycle_fee_refund') || 0;
    const automobileTaxRefund = this.getFormValue(vehicle._id, 'automobile_tax_refund') || 0;
    const weightTaxRefund = this.getFormValue(vehicle._id, 'weight_tax') || 0;
    const carInsuranceRefund = this.getFormValue(vehicle._id, 'car_insurance_refund') || 0;

    total += +recycleFeeRefund;
    total += +automobileTaxRefund;
    total += +weightTaxRefund;
    total += +carInsuranceRefund;

    return total;

  }

  // Export Excel method
  exportExcel(): void {
    this.isExportingExcel = true;

    // Prepare filters for API call
    const apiFilters: Record<string, any> = { ...this.filters };

    // Remove empty values
    for (const key in apiFilters) {
      if (apiFilters[key] === '' || apiFilters[key] === null) {
        delete apiFilters[key];
      }
    }

    // Prepare the request body
    const requestBody = {
      ...apiFilters,
      type: 'vehicle',
      panel_filters: 'true',
      append_type: 'refund',
      vehicle_type: 'refund',
      excel_name: 'Refund Report'
    };

    this.subscriptions.push(
      this._generalService.generateExcelPost(requestBody).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }
}
